import useSWR, { useSWRConfig } from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { fetcherWithDataKey } from '@/utils/services';
import {
  createLifecycleBoard,
  deleteLifecycleBoard,
} from '../LifecycleBoard.services';

const useLifecycleBoards = (searchParams = {}, swrOptions) => {
  const ownerOrgId = useOwnerOrgId();
  const { user } = useCurrentUser();
  const { mutate: globalMutate } = useSWRConfig();

  const key = ownerOrgId
    ? `boards?${joinNonEmptyParams({ ownerOrgId, ...searchParams })}`
    : null;
  const { data, error, isValidating, mutate } = useSWR(
    key,
    fetcherWithDataKey('boards'),
    swrOptions,
  );

  // The `mutate` calls for `create` and `remove` are doing a bit of local state
  // maintenance as Elastic Search results do not immediately reflect the
  // addition or removal of new boards.
  const create = async ({ boardTitle, columns }) => {
    const res = await createLifecycleBoard({
      boardTitle,
      columns,
      createdBy: {
        displayName: user?.displayName,
        userId: user?.userId,
      },
      ownerOrgId,
    });
    mutate((oldVal) => [res.data?.board, ...oldVal], false);
    return res.data?.board;
  };

  const remove = async (boardId) => {
    await deleteLifecycleBoard(boardId);
    globalMutate(`board/${boardId}`, undefined, { revalidate: false });
    // TODO(jon): For some reason, trying to do a function update like in the
    // `create` method above always results in `oldVal` being undefined, so
    // instead we're going to filter off of `data`.
    return mutate(
      (data || []).filter((board) => board.boardId !== boardId),
      { revalidate: false },
    );
  };

  return { create, data, error, isValidating, remove };
};

export default useLifecycleBoards;
